import { toast } from '@solinkcorp/solink-components';
import { type AiNotification, type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import ConfirmDialog from 'components/HomePage/Aspen/ConfirmDialog';
import useAiNotifications from 'components/SpotCheckPanel/hooks/useAiNotifcations';
import { useSpotChecks } from 'components/SpotCheckPanel/hooks/useSpotChecks';
import useLocale from 'src/hooks/useLocale';
import { useSelector } from 'stores/store';
import LocaleUtil from 'utils/LocaleUtil';

interface SpotCheckDeleteConfirmationModalProps {
  isModalOpen: boolean;
  closeModal: () => void;
  openModal: () => void;
  spotCheck: ScheduledActivityEntity | undefined;
  eventSpotCheck: AiNotification | undefined;
}

export default function SpotCheckDeleteConfirmationModal(
  props: SpotCheckDeleteConfirmationModalProps,
) {
  const { isModalOpen, closeModal, spotCheck, eventSpotCheck } = props;
  const locale = useLocale();

  const { spotCheckMode } = useSelector((state) => ({
    spotCheckMode: state.filter.spotCheckMode,
  }));

  const isEventSpotCheck = spotCheckMode === 'event';

  const text = LocaleUtil.getLocalizedStr({
    string: locale.general.deleteConfirmMessage,
    replacements: {
      name: isEventSpotCheck
        ? (eventSpotCheck?.metadata?.title ?? locale.scheduledAIChat.eventSpotCheck)
        : spotCheck?.title,
    },
  });

  const spotChecks = useSpotChecks();
  const eventSpotChecks = useAiNotifications();

  const handleConfirm = async () => {
    try {
      if (isEventSpotCheck) {
        if (!eventSpotCheck?.id) return;
        await eventSpotChecks.deleteAiNotification(eventSpotCheck.id);
      } else {
        if (!spotCheck) return;
        await spotChecks.turnOffScheduledActivity(spotCheck.id);
      }
      toast.success(locale.scheduledAIChat.successDeleteSpotCheck);
    } catch (error) {
      toast.error(locale.scheduledAIChat.failedToDeleteSpotCheck);
      throw error;
    }
  };

  return (
    <ConfirmDialog
      open={isModalOpen}
      onClose={closeModal}
      title={locale.scheduledAIChat.deleteSpotCheck}
      description={text}
      cancelText={locale.general.cancel}
      confirmText={locale.general.delete}
      confirmVariant="destructive"
      onConfirm={handleConfirm}
      dataTestId="spot-check-delete-confirmation"
    />
  );
}
